import { useState, useEffect, useRef } from 'react';
import { DECK_INFO, CATEGORIES } from '../mock/gameData';
import { useToast } from '../hooks/use-toast';
import gameSounds from '../utils/gameSounds';

export const WHICHING_STATES = {
  CATEGORY_SELECTION: 'category_selection',
  DECK_SELECTION: 'deck_selection',
  PLAYING: 'playing',
  GAME_OVER: 'game_over'
};

// Seeded random number generator for daily challenge
const seededRandom = (seed) => {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};

// Seeded shuffle for consistent daily card order
const seededShuffle = (array, seed) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Get today's date seed
const getTodaySeed = () => {
  const today = new Date().toISOString().split('T')[0];
  return today.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
};

export const useWhichingGame = ({ user, challengeData, initialDeck, isDailyChallenge = false }) => {
  const [gameState, setGameState] = useState(
    initialDeck ? WHICHING_STATES.PLAYING : WHICHING_STATES.CATEGORY_SELECTION
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDeck, setSelectedDeck] = useState(initialDeck || null);
  const [score, setScore] = useState(0);
  const [strikes, setStrikes] = useState(0);
  const [maxStrikes, setMaxStrikes] = useState(3);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [showStreakBonus, setShowStreakBonus] = useState(false);
  const [xRayVision, setXRayVision] = useState(false);
  const [currentCards, setCurrentCards] = useState([]);
  const [isWinner, setIsWinner] = useState(false);
  const [winStreak, setWinStreak] = useState(0);
  const [maxWinStreak, setMaxWinStreak] = useState(0);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isCreatingChallenge, setIsCreatingChallenge] = useState(false);
  const [animatingCard, setAnimatingCard] = useState(null);
  const [cardResult, setCardResult] = useState(null);
  const { toast } = useToast();

  // Handle challenge - auto-start with challenge deck
  useEffect(() => {
    if (challengeData) {
      setSelectedDeck(challengeData.deck_type);
      setGameState(WHICHING_STATES.PLAYING);
    }
  }, [challengeData]);

  // Initialize sound state from localStorage
  useEffect(() => {
    const savedSoundPref = localStorage.getItem('whiching_sound_enabled');
    if (savedSoundPref !== null) {
      const enabled = savedSoundPref === 'true';
      setSoundEnabled(enabled);
      gameSounds.setEnabled(enabled);
    }
  }, []);

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    gameSounds.setEnabled(newState);
    localStorage.setItem('whiching_sound_enabled', String(newState));
  };

  // Track daily challenge pairs sequence
  const dailyPairIndexRef = useRef(0);
  const dailyPairsRef = useRef([]);

  // Generate all unique pairs from a deck
  const generateAllPairs = (cards) => {
    const pairs = [];
    for (let i = 0; i < cards.length; i++) {
      for (let j = i + 1; j < cards.length; j++) {
        pairs.push([cards[i], cards[j]]);
      }
    }
    return pairs;
  };

  const getRandomCards = (deck) => {
    const deckInfo = DECK_INFO[deck];
    if (!deckInfo || !deckInfo.cards || deckInfo.cards.length < 2) {
      console.error('Invalid deck or not enough cards');
      return [];
    }
    
    // For daily challenge, use seeded sequence of ALL possible pairs
    if (isDailyChallenge) {
      // Initialize shuffled pairs on first call
      if (dailyPairsRef.current.length === 0) {
        const seed = getTodaySeed();
        const allPairs = generateAllPairs(deckInfo.cards);
        dailyPairsRef.current = seededShuffle(allPairs, seed);
        dailyPairIndexRef.current = 0;
      }
      
      const pairs = dailyPairsRef.current;
      const idx = dailyPairIndexRef.current;
      
      // Get next pair (wraps around if needed, but with n*(n-1)/2 pairs this is rare)
      const pair = pairs[idx % pairs.length];
      
      // Randomly swap order within pair using seed for consistency
      const swapOrder = seededRandom(getTodaySeed() + idx) > 0.5;
      const [firstCard, secondCard] = swapOrder ? [pair[1], pair[0]] : pair;
      
      // Advance index for next round
      dailyPairIndexRef.current = idx + 1;
      
      return [firstCard, secondCard];
    }
    
    // Regular random mode
    const allCards = [...deckInfo.cards];
    const firstIndex = Math.floor(Math.random() * allCards.length);
    let secondIndex;
    do {
      secondIndex = Math.floor(Math.random() * allCards.length);
    } while (secondIndex === firstIndex);
    
    return [allCards[firstIndex], allCards[secondIndex]];
  };

  const handleCategorySelect = (category) => {
    if (category === CATEGORIES.RANDOM) {
      const allDecks = Object.keys(DECK_INFO);
      const randomDeck = allDecks[Math.floor(Math.random() * allDecks.length)];
      setSelectedDeck(randomDeck);
      setGameState(WHICHING_STATES.PLAYING);
    } else {
      setSelectedCategory(category);
      setGameState(WHICHING_STATES.DECK_SELECTION);
    }
  };

  const backToCategorySelection = () => {
    setSelectedCategory(null);
    setGameState(WHICHING_STATES.CATEGORY_SELECTION);
  };

  const handleDeckSelect = (deckType) => {
    setSelectedDeck(deckType);
    setScore(0);
    setStrikes(0);
    setMaxStrikes(3);
    setConsecutiveCorrect(0);
    setWinStreak(0);
    setMaxWinStreak(0);
    setIsWinner(false);
    setScoreSubmitted(false);
    setXRayVision(false);
    setShowStreakBonus(false);
    setCurrentCards(getRandomCards(deckType));
    setGameState(WHICHING_STATES.PLAYING);
    
    // Track anonymous play for "Recently Played" section
    const deckInfo = DECK_INFO[deckType];
    if (deckInfo) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      fetch(`${backendUrl}/api/track-play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deck_type: deckType,
          deck_name: deckInfo.name
        })
      }).catch(() => {}); // Fire and forget, don't block gameplay
    }
  };

  // Initialize cards when entering playing state
  useEffect(() => {
    if (gameState === WHICHING_STATES.PLAYING && selectedDeck && currentCards.length === 0) {
      setCurrentCards(getRandomCards(selectedDeck));
    }
  }, [gameState, selectedDeck]);

  const submitScore = async (finalScore, finalWinStreak, didWin) => {
    if (!user || scoreSubmitted) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const deckInfo = DECK_INFO[selectedDeck];
      const sessionToken = localStorage.getItem('session_token');
      
      const headers = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${backendUrl}/api/scores`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          deck_type: selectedDeck,
          deck_name: deckInfo?.name || 'Unknown Deck',
          score: finalScore,
          win_streak: finalWinStreak,
          is_winner: didWin,
          is_daily_challenge: isDailyChallenge
        })
      });
      
      if (response.ok) {
        setScoreSubmitted(true);
      }
    } catch (error) {
      console.error('Failed to submit score:', error);
    }
  };

  const handleCardClick = (selectedCard) => {
    if (animatingCard) return;
    
    const otherCard = currentCards.find(c => c.id !== selectedCard.id);
    
    const isCorrect = selectedCard.rank < otherCard.rank;
    
    setAnimatingCard(selectedCard.id);
    setCardResult(isCorrect ? 'winner' : 'loser');

    if (isCorrect) {
      gameSounds.playCorrect();
      const newScore = score + 1;
      const newWinStreak = winStreak + 1;
      const newConsecutive = consecutiveCorrect + 1;
      
      setScore(newScore);
      setWinStreak(newWinStreak);
      if (newWinStreak > maxWinStreak) {
        setMaxWinStreak(newWinStreak);
      }
      setConsecutiveCorrect(newConsecutive);

      // Check for 4-in-a-row X-Ray Vision bonus (will activate on NEXT cards)
      const shouldActivateXRay = newConsecutive === 4;
      if (shouldActivateXRay) {
        gameSounds.playXRayVision();
        toast({
          title: "👁️ X-Ray Vision Activated!",
          description: "Next matchup will show card ranks!",
        });
      }

      // Check for 7-in-a-row bonus strike
      if (newConsecutive === 7) {
        gameSounds.playStreakBonus();
        setMaxStrikes(prev => prev + 1);
        setShowStreakBonus(true);
        setConsecutiveCorrect(0);
        toast({
          title: "🔥 7 in a row! Bonus Strike!",
          description: "You earned an extra life!",
        });
        setTimeout(() => setShowStreakBonus(false), 2000);
      }

      // Check for score milestones
      if (newScore === 10 || newScore === 20 || newScore === 25 || newScore === 50) {
        gameSounds.playMilestone();
      }

      // Check for win condition at 55 points
      if (newScore >= 55) {
        gameSounds.playVictory();
        setIsWinner(true);
        setGameState(WHICHING_STATES.GAME_OVER);
        submitScore(newScore, newWinStreak, true);
        return;
      }

      // Capture current xRayVision state for closure
      const currentXRayVision = xRayVision;
      
      setTimeout(() => {
        setAnimatingCard(null);
        setCardResult(null);
        // Activate X-Ray Vision now that new cards are loading
        if (shouldActivateXRay) {
          setXRayVision(true);
        } else if (currentXRayVision) {
          // Turn off X-Ray Vision after it was used
          setXRayVision(false);
        }
        setCurrentCards(getRandomCards(selectedDeck));
      }, 1000);
    } else {
      gameSounds.playWrong();
      const newStrikes = strikes + 1;
      setStrikes(newStrikes);
      setWinStreak(0);
      setConsecutiveCorrect(0);
      
      const margin = selectedCard.rank - otherCard.rank;
      let message = "Wrong!";
      if (margin <= 2) {
        message = "So close! Just off by " + margin;
      } else if (margin <= 5) {
        message = "Not quite! Off by " + margin;
      } else {
        message = "Way off! Difference of " + margin;
      }
      
      toast({
        title: "❌ " + message,
        variant: "destructive"
      });

      if (newStrikes >= maxStrikes) {
        gameSounds.playGameOver();
        setTimeout(() => {
          setAnimatingCard(null);
          setCardResult(null);
          setXRayVision(false);
          setGameState(WHICHING_STATES.GAME_OVER);
          submitScore(score, maxWinStreak, false);
        }, 1500);
      } else {
        setTimeout(() => {
          setAnimatingCard(null);
          setCardResult(null);
          setXRayVision(false);
          setCurrentCards(getRandomCards(selectedDeck));
        }, 1500);
      }
    }
  };

  const playAgain = () => {
    setScore(0);
    setStrikes(0);
    setMaxStrikes(3);
    setConsecutiveCorrect(0);
    setWinStreak(0);
    setMaxWinStreak(0);
    setIsWinner(false);
    setScoreSubmitted(false);
    setXRayVision(false);
    setShowStreakBonus(false);
    setCurrentCards(getRandomCards(selectedDeck));
    setGameState(WHICHING_STATES.PLAYING);
  };

  const backToDeckSelection = () => {
    setSelectedDeck(null);
    setScore(0);
    setStrikes(0);
    setMaxStrikes(3);
    setConsecutiveCorrect(0);
    setWinStreak(0);
    setMaxWinStreak(0);
    setIsWinner(false);
    setScoreSubmitted(false);
    setXRayVision(false);
    setShowStreakBonus(false);
    setCurrentCards([]);
    setGameState(WHICHING_STATES.DECK_SELECTION);
  };

  const shareScore = async () => {
    const deckInfo = DECK_INFO[selectedDeck];
    const deckName = deckInfo?.name || 'Pop Culture Wars';
    
    const shareText = isWinner 
      ? `🏆 I conquered ${deckName} with a score of ${score}! Can you beat that?`
      : `🎮 I scored ${score} on ${deckName} with a ${maxWinStreak} streak! 🔥 Can you do better?`;
    
    const shareUrl = window.location.origin;
    const fullShareText = `${shareText}\n\nPlay now: ${shareUrl}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pop Culture Wars - Which\'ing Hour',
          text: shareText,
          url: shareUrl
        });
        toast({ title: "Shared!", description: "Thanks for sharing!" });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed, falling back to clipboard');
        }
      }
    }
    
    try {
      await navigator.clipboard.writeText(fullShareText);
      toast({ title: "Copied to clipboard!", description: "Share it with your friends!" });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = fullShareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({ title: "Copied to clipboard!", description: "Share it with your friends!" });
    }
  };

  const createChallenge = async () => {
    setIsCreatingChallenge(true);
    
    const deckInfo = DECK_INFO[selectedDeck];
    const challengerName = user?.nickname || user?.name || 'Someone';
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/challenges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deck_type: selectedDeck,
          deck_name: deckInfo?.name || 'Unknown Deck',
          score: score,
          streak: maxWinStreak,
          challenger_name: challengerName
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const challengeUrl = `${window.location.origin}?challenge=${data.challenge_id}`;
        const challengeText = `🎯 ${challengerName} challenges you to beat their score of ${score} on ${deckInfo?.name}!\n\nAccept the challenge: ${challengeUrl}`;
        
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Pop Culture Wars Challenge!',
              text: `${challengerName} challenges you to beat their score of ${score} on ${deckInfo?.name}!`,
              url: challengeUrl
            });
            toast({ title: "Challenge sent!", description: "See if they can beat your score!" });
            setIsCreatingChallenge(false);
            return;
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.log('Share failed, falling back to clipboard');
            }
          }
        }
        
        await navigator.clipboard.writeText(challengeText);
        toast({ title: "Challenge link copied!", description: "Send it to a friend to challenge them!" });
      } else {
        throw new Error('Failed to create challenge');
      }
    } catch (error) {
      console.error('Challenge creation failed:', error);
      toast({ title: "Couldn't create challenge", description: "Please try again", variant: "destructive" });
    }
    
    setIsCreatingChallenge(false);
  };

  return {
    // State
    gameState,
    selectedCategory,
    selectedDeck,
    score,
    strikes,
    maxStrikes,
    consecutiveCorrect,
    showStreakBonus,
    xRayVision,
    currentCards,
    isWinner,
    winStreak,
    maxWinStreak,
    scoreSubmitted,
    soundEnabled,
    isCreatingChallenge,
    animatingCard,
    cardResult,
    deckInfo: selectedDeck ? DECK_INFO[selectedDeck] : null,
    isDailyChallenge,
    
    // Actions
    toggleSound,
    handleCategorySelect,
    backToCategorySelection,
    handleDeckSelect,
    handleCardClick,
    playAgain,
    backToDeckSelection,
    shareScore,
    createChallenge,
  };
};
